import { useEffect, useState } from 'react';
import { staffApi, type ReferralMilestoneStatus, type ReferralProgramLevel } from '../../api/staff';
import { Card } from '../../components/ui/Card';
import { formatMoney } from '../../utils/numbers';
import { usePricing } from '../../hooks/usePricing';
import './staff.css';

const LEVEL_LABELS: Record<string, string> = {
  bronze: 'Bronce',
  silver: 'Plata',
  gold: 'Oro',
  diamond: 'Diamante',
};

function formatClientRange(level: ReferralProgramLevel): string {
  if (level.maxActiveClients != null) {
    return `${level.minActiveClients}–${level.maxActiveClients}`;
  }
  return `≥${level.minActiveClients}`;
}

function milestoneStatusLabel(item: ReferralMilestoneStatus): string {
  if (item.estado === 'pagado') return 'Pagado';
  if (item.estado === 'incluido_en_payout') return 'En liquidación';
  if (item.reached) return 'Conseguido';
  return 'Pendiente';
}

export function StaffReferralsPage() {
  const pricing = usePricing();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [referralUrl, setReferralUrl] = useState('');
  const [activeClients, setActiveClients] = useState(0);
  const [milestones, setMilestones] = useState<ReferralMilestoneStatus[]>([]);
  const [levels, setLevels] = useState<ReferralProgramLevel[]>([]);
  const [copied, setCopied] = useState<'code' | 'url' | null>(null);

  useEffect(() => {
    setLoading(true);
    setError('');
    Promise.all([
      staffApi.getMyReferralCode().catch(() => null),
      staffApi.getReferralMilestones(),
      staffApi.getReferralProgram(),
    ])
      .then(([codeRes, milestonesRes, programRes]) => {
        if (codeRes?.referralCode) {
          setReferralCode(codeRes.referralCode.codigo);
          setReferralUrl(codeRes.referralCode.url ?? '');
        }
        setActiveClients(milestonesRes.activeClients);
        setMilestones(milestonesRes.milestones);
        setLevels(programRes.levels);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Error al cargar referidos'))
      .finally(() => setLoading(false));
  }, []);

  async function copyText(text: string, kind: 'code' | 'url') {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      setError('No se pudo copiar al portapapeles');
    }
  }

  if (loading) return <p>Cargando referidos…</p>;
  if (error) return <p style={{ color: 'var(--color-danger)' }}>{error}</p>;

  const nextMilestone = milestones.find((m) => !m.reached);

  return (
    <div>
      <div className="staff-page-header">
        <h1>Referidos</h1>
      </div>

      <div className="staff-earnings-grid">
        <Card title="Tu código">
          {referralCode ? (
            <>
              <p style={{ margin: '0 0 0.5rem', fontSize: '1.5rem', fontWeight: 800, letterSpacing: '0.05em' }}>
                {referralCode}
              </p>
              {referralUrl ? (
                <p className="muted" style={{ margin: '0 0 1rem', fontSize: '0.85rem', wordBreak: 'break-all' }}>
                  {referralUrl}
                </p>
              ) : null}
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button type="button" className="staff-referral-copy" onClick={() => void copyText(referralCode, 'code')}>
                  {copied === 'code' ? 'Copiado' : 'Copiar código'}
                </button>
                {referralUrl ? (
                  <button type="button" className="staff-referral-copy" onClick={() => void copyText(referralUrl, 'url')}>
                    {copied === 'url' ? 'Copiado' : 'Copiar enlace'}
                  </button>
                ) : null}
              </div>
            </>
          ) : (
            <p className="muted" style={{ margin: 0 }}>
              No tienes código de referido asignado. Contacta con administración.
            </p>
          )}
        </Card>

        <Card title="Tu progreso">
          <div className="staff-earnings-breakdown">
            <div>
              <span className="muted">Clientes activos</span>
              <strong>{activeClients}</strong>
            </div>
            <div>
              <span className="muted">Próximo hito</span>
              <strong>
                {nextMilestone
                  ? `${nextMilestone.activeClients} clientes (${formatMoney(nextMilestone.bonusAmount)})`
                  : 'Todos conseguidos'}
              </strong>
            </div>
          </div>
        </Card>
      </div>

      <div className="staff-earnings-grid" style={{ marginTop: '1rem' }}>
        <Card title="Niveles">
          <p className="muted" style={{ margin: '0 0 0.75rem', fontSize: '0.9rem' }}>
            Cada TPV activo con tu código cuenta como cliente activo ({pricing.formatted}, IVA incluido).
          </p>
          <ul className="staff-referral-list">
            {levels.map((level) => (
              <li key={level.level}>
                <strong>{LEVEL_LABELS[level.level] ?? level.level}</strong>
                <span>{formatClientRange(level)} clientes</span>
              </li>
            ))}
          </ul>
        </Card>

        <Card title="Hitos (pago único)">
          <p className="muted" style={{ margin: '0 0 0.75rem', fontSize: '0.9rem' }}>
            Bonificaciones al alcanzar cada umbral de clientes activos referidos.
          </p>
          <div className="staff-payout-list">
            {milestones.map((item) => (
              <div key={item.activeClients} className="staff-payout-item">
                <div>
                  <strong>{item.activeClients} clientes activos</strong>
                  <p className="muted" style={{ margin: '0.2rem 0 0', fontSize: '0.85rem' }}>
                    {formatMoney(item.bonusAmount)} · {milestoneStatusLabel(item)}
                  </p>
                  {!item.reached ? (
                    <p className="muted" style={{ margin: '0.2rem 0 0', fontSize: '0.85rem' }}>
                      Faltan {item.activeClients - activeClients} cliente
                      {item.activeClients - activeClients === 1 ? '' : 's'}
                    </p>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
